// layouts/auth/PasswordRecovery.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// MUI
import {
  Card,
  Grid,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Layouts (si prefieres un layout distinto para auth, cámbialo aquí)
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { recoverPassword } from "../autenticacion/usuarios/services/users";

/* ---------------- helpers ---------------- */

function stripHtml(s) {
  return String(s || "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function extractApiErrors(err) {
  const out = [];
  const res = err?.response;
  const d = res?.data;

  if (typeof d === "string") out.push(stripHtml(d));
  if (d?.message) out.push(d.message);
  if (Array.isArray(d?.errors)) d.errors.forEach((e) => out.push(e?.message || e?.msg || JSON.stringify(e)));
  if (Array.isArray(d) && !d.errors) d.forEach((e) => out.push(typeof e === "string" ? e : JSON.stringify(e)));
  if (res?.status) out.push(`HTTP ${res.status}`);

  if (!out.length) out.push(err?.message || "Error de servidor");
  return [...new Set(out)].filter(Boolean);
}

const safeString = (x) => {
  try {
    if (x == null) return "";
    if (typeof x === "string") return x;
    if (x?.message && typeof x.message === "string") return x.message;
    if (x?.instancePath || x?.schemaPath || x?.keyword) {
      return `${x.instancePath || ""} ${x.message || ""}`.trim() || JSON.stringify(x);
    }
    return JSON.stringify(x);
  } catch {
    return String(x);
  }
};

const meetsPolicy = (pwd) => {
  if (!pwd) return false;
  // Reglas mínimas (ajusta a tu política real)
  const lenOK = pwd.length >= 8;
  const upper = /[A-ZÁÉÍÓÚÜ]/.test(pwd);
  const lower = /[a-záéíóúü]/.test(pwd);
  const digit = /\d/.test(pwd);
  return lenOK && upper && lower && digit;
};

/* ---------------- component ---------------- */

export default function PasswordRecovery() {
  const { key } = useParams(); // /auth/recovery/:key
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");

  const pwdPolicyOK = useMemo(() => meetsPolicy(password), [password]);
  const samePwd = useMemo(() => password && password2 && password === password2, [password, password2]);

  const disabled = submitting || !pwdPolicyOK || !samePwd || !key;

  // 👇 Ayuda visual: muestra qué condición está bloqueando el botón
 const disabledReason = !key
   ? "Falta la clave del enlace."
   : !pwdPolicyOK
   ? "La contraseña no cumple la política."
   : !samePwd
   ? "Las contraseñas no coinciden."
   : submitting
   ? "Enviando…"
   : "";

  // (Opcional) Validar que venga key
  useEffect(() => {
    if (!key) {
      setErrors(["Link inválido o incompleto. Falta la clave de recuperación."]);
    }
  }, [key]);

  const handleSubmit = async () => {
    setErrors([]);
    setSuccessMsg("");

    // Validaciones rápidas
    if (!key) return setErrors(["Clave de recuperación inválida."]);
    if (!pwdPolicyOK) return setErrors(["La contraseña no cumple la política mínima."]);
    if (!samePwd) return setErrors(["Las contraseñas no coinciden."]);

    try {
      setSubmitting(true);

      // Llamada al backend
      await recoverPassword({ key, password, repeatPassword: password2 });

      setSuccessMsg("¡Contraseña actualizada! Ya puedes iniciar sesión.");
      // (Opcional) Si tu backend invalida la sesión/flag, puedes redirigir tras 2s:
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (e) {
      console.error("[PasswordRecovery] ERROR:", e);
      setErrors(extractApiErrors(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card sx={{ p: 3, maxWidth: 720, mx: "auto" }}>
          <SoftBox mb={3} display="flex" alignItems="center" justifyContent="space-between">
            <SoftTypography variant="h6">Restablecer contraseña</SoftTypography>
            <SoftButton variant="outlined" color="secondary" onClick={() => navigate(-1)}>
              Volver
            </SoftButton>
          </SoftBox>

          <Alert severity="info" sx={{ mb: 2 }}>
            Ingresa tu nueva contraseña y confírmala. Recuerda que debe tener al menos 8 caracteres,
            incluir mayúsculas, minúsculas y números.
          </Alert>

          {submitting && <LinearProgress sx={{ mb: 2 }} />}

          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.map((m, i) => (
                <div key={i}>{safeString(m)}</div>
              ))}
            </Alert>
          )}

          {successMsg && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMsg}
            </Alert>
          )}

        <form
        onSubmit={(e) => {
            e.preventDefault();
            if (!disabled) handleSubmit();
        }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nueva contraseña"
                type={showPwd ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                helperText={
                  password
                    ? pwdPolicyOK
                      ? "La contraseña cumple la política."
                      : "Mín. 8 caracteres; al menos 1 mayúscula, 1 minúscula y 1 número."
                    : " "
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPwd((v) => !v)} edge="end" size="small">
                        {showPwd ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirma la nueva contraseña"
                type={showPwd2 ? "text" : "password"}
                name="password2"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                autoComplete="new-password"
                required
                error={!!password2 && !samePwd}
                helperText={!!password2 && !samePwd ? "Las contraseñas no coinciden" : " "}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPwd2((v) => !v)} edge="end" size="small">
                        {showPwd2 ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} display="flex" gap={1} mt={1}>
              <SoftButton
                type="submit"
                disabled={disabled}
                color="dark"
                variant="gradient"
              >
                {submitting ? "Guardando…" : "Actualizar contraseña"}
              </SoftButton>
              <SoftButton variant="outlined" color="secondary" onClick={() => navigate("/login")}>
                Ir a Iniciar sesión
              </SoftButton>
            </Grid>
               {/* Motivo de deshabilitado, si aplica */}
                {disabled && !submitting && (
                    <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                    {disabledReason}
                    </div>
                )}
          </Grid>
        </form>
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}
